import React, { useState, useCallback, useMemo, useEffect } from 'react';

import { Staff } from '../../types';
import { StaffFilterSelection } from '../../components/StaffFilter/types';
import { getCurrentUser } from '../../api';
import useFetch from '../../hooks/useFetch';

import { StaffFilterContext } from './StaffFilterContext';
import { useStaffList } from './hooks/useStaffList';
import { useStaffUrlSync } from './hooks/useStaffUrlSync';

const StaffFilterContextProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [selectedStaff, setSelectedStaffState] = useState<Staff | null>(null);
  const [selection, setSelectionState] = useState<StaffFilterSelection>({ type: 'everyone' });
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false);

  // Fetch current user
  const fetchCurrentUser = useCallback(() => getCurrentUser(), []);
  const { data: currentUserData, isLoading: isLoadingCurrentUser } = useFetch(fetchCurrentUser, []);

  // Convert current user data to Staff type
  const currentUser: Staff | null = useMemo(() => {
    if (!currentUserData) return null;
    return {
      id: currentUserData.id,
      email: currentUserData.email,
      full_name: currentUserData.full_name,
    };
  }, [currentUserData]);

  // Staff list data and actions
  const { staffList, isLoading, hasMore, searchQuery, search, loadMore, loadInitial } = useStaffList();

  // Set default selection to current_user once loaded (matching "Assigned" behavior)
  // Skip if URL has staff param - let URL sync handle initialization
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasStaffUrlParam = urlParams.has('staff');

    if (currentUser && !hasInitializedSelection && !hasStaffUrlParam) {
      setSelectionState({ type: 'current_user', user: currentUser });
      setHasInitializedSelection(true);
    }
  }, [currentUser, hasInitializedSelection]);

  const setSelectedStaff = useCallback((staff: Staff | null) => {
    setSelectedStaffState(staff);
    // Also update selection for backwards compatibility
    if (staff) {
      setSelectionState({ type: 'specific_staff', staff });
    } else {
      setSelectionState({ type: 'everyone' });
    }
  }, []);

  const setSelection = useCallback((newSelection: StaffFilterSelection) => {
    // Mark as initialized to prevent default selection from overwriting URL-restored values
    setHasInitializedSelection(true);
    setSelectionState(newSelection);
    // Also update selectedStaff for backwards compatibility
    if (newSelection.type === 'specific_staff') {
      setSelectedStaffState(newSelection.staff);
    } else {
      setSelectedStaffState(null);
    }
  }, []);

  // URL synchronization (both directions)
  useStaffUrlSync({
    selection,
    setSelection,
    loadInitial,
    setSelectedStaff,
  });

  const contextValue = useMemo(
    () => ({
      currentUser,
      isLoadingCurrentUser,
      selection,
      setSelection,
      isSelectionInitialized: hasInitializedSelection,
      selectedStaff,
      setSelectedStaff,
      staffList,
      isLoadingStaff: isLoading,
      hasMoreStaff: hasMore,
      searchStaff: search,
      loadMoreStaff: loadMore,
      searchQuery,
    }),
    [
      currentUser,
      isLoadingCurrentUser,
      selection,
      setSelection,
      hasInitializedSelection,
      selectedStaff,
      setSelectedStaff,
      staffList,
      isLoading,
      hasMore,
      search,
      loadMore,
      searchQuery,
    ],
  );

  return <StaffFilterContext.Provider value={contextValue}>{children}</StaffFilterContext.Provider>;
};

export default StaffFilterContextProvider;
